/**
 * Which cells of a 3D inversion model are drawn: valid cells (georeferenced, resistivity > 0, above the
 * DOI), then the slice / crop filters (the population), split into inside and outside the visible
 * range. Colours come from `colour` (a lib/model-colour scale: levels, table, banded) or, without one,
 * the log-resistivity ramp over the valid cells. Pure, no three.js.
 */
import { makeDoiMask } from './inversion-results';
import { rampColor } from './iso-scene';

export const BLOCK_CAP = 500000; // beyond this many cells blocks fall back to points

const num = Number.isFinite;
const NO_FILTER = { threshold: [0, 1], sliceAxis: 'none', slicePos: 0.5, sliceThickness: 0.15, cropX: [0, 1], cropZ: [0, 1] };

/** True when the first cell carries the extents blocks are drawn from. */
export function hasExtents(cells) {
  const c = cells?.[0];
  return !!c && [c.x0, c.x1, c.y0, c.y1, c.top, c.bottom].every(num);
}

/** 'blocks' | 'points' actually used: blocks need extents and at most BLOCK_CAP cells. */
export function effectiveMode(wanted, cells, nValid = cells?.length || 0) {
  return wanted !== 'points' && hasExtents(cells) && nValid <= BLOCK_CAP ? 'blocks' : 'points';
}

/**
 * `alpha` (per valid cell) is the colour scale's opacity curve, times `outsideOpacity` outside the visible range.
 * @returns {{ cells, colors, alpha, popAlpha, east, north, inside: Int32Array, outside: Int32Array, popCells, inMask, visible, mode, capped }}
 *  cells/colors/east/north are per valid cell; inside/outside index into them; popCells/inMask
 *  (1 = in range) describe the filtered population (cells that pass DOI, slice and crop).
 */
export function selectVoxels(entry, origin, { doi = {}, mode = 'blocks', colour = null, outsideOpacity = 0, ...filters } = {}) {
  const f = { ...NO_FILTER, ...filters };
  const all = entry.cells || [];
  const belowDoi = makeDoiMask(all, { mask: doi.mask, threshold: doi.threshold, fallbackDoi: entry.model?.doi });
  const cells = all.filter((c) => c.lon !== undefined && c.resistivity > 0 && !belowDoi(c));
  const n = cells.length;
  const mLat = 110540;
  const mLon = 111320 * Math.cos((origin.lat0 * Math.PI) / 180);
  const east = new Float32Array(n), north = new Float32Array(n);
  const logs = new Float32Array(n);
  let tMin = Infinity, tMax = -Infinity;
  cells.forEach((c, i) => {
    east[i] = (c.lon - origin.lon0) * mLon;
    north[i] = (c.lat - origin.lat0) * mLat;
    logs[i] = Math.log10(c.resistivity);
    if (logs[i] < tMin) tMin = logs[i];
    if (logs[i] > tMax) tMax = logs[i];
  });
  const span = tMax > tMin ? tMax - tMin : 1;
  const colors = new Float32Array(n * 3);
  const alpha = new Float32Array(n);
  const t = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    t[i] = (logs[i] - tMin) / span;
    const [r, g, b] = colour ? colour.rgb(cells[i].resistivity) : rampColor(t[i]);
    alpha[i] = colour?.alpha ? colour.alpha(cells[i].resistivity) : 1;
    colors[i * 3] = r / 255; colors[i * 3 + 1] = g / 255; colors[i * 3 + 2] = b / 255;
  }

  // slice / crop fractions over the valid cells' extent (east, north, depth)
  const ext = (arr) => {
    let lo = Infinity, hi = -Infinity;
    for (let i = 0; i < arr.length; i++) { if (arr[i] < lo) lo = arr[i]; if (arr[i] > hi) hi = arr[i]; }
    return [lo, (hi - lo) || 1];
  };
  const [e0, eSpan] = ext(east), [n0, nSpan] = ext(north);
  const depths = Float32Array.from(cells, (c) => c.depth || 0);
  const [d0, dSpan] = ext(depths);
  const sliced = f.sliceAxis !== 'none';
  const cropped = f.cropX[0] > 0 || f.cropX[1] < 1 || f.cropZ[0] > 0 || f.cropZ[1] < 1;

  const inside = [], outside = [], popCells = [], inMask = [], popAlpha = [];
  const visible = new Uint8Array(n); // per valid cell, ignoring the thickness filter: 0 cropped, 1 in range, 2 outside the range (slice planes)
  const eps = 1e-6;
  for (let i = 0; i < n; i++) {
    if (cropped) {
      const fx = (east[i] - e0) / eSpan, fz = (north[i] - n0) / nSpan;
      if (fx < f.cropX[0] - eps || fx > f.cropX[1] + eps || fz < f.cropZ[0] - eps || fz > f.cropZ[1] + eps) continue;
    }
    const inRange = t[i] >= f.threshold[0] - eps && t[i] <= f.threshold[1] + eps;
    visible[i] = inRange ? 1 : 2;
    if (!inRange) alpha[i] *= outsideOpacity;
    if (sliced) {
      const fv = f.sliceAxis === 'depth' ? (depths[i] - d0) / dSpan : f.sliceAxis === 'x' ? (east[i] - e0) / eSpan : (north[i] - n0) / nSpan;
      if (Math.abs(fv - f.slicePos) > f.sliceThickness / 2) continue;
    }
    (inRange ? inside : outside).push(i);
    popCells.push(cells[i]);
    inMask.push(inRange ? 1 : 0);
    popAlpha.push(alpha[i]);
  }
  return {
    cells, colors, alpha, popAlpha, east, north, inside: Int32Array.from(inside), outside: Int32Array.from(outside),
    popCells, inMask, visible, mode: effectiveMode(mode, all, n), capped: mode !== 'points' && hasExtents(all) && n > BLOCK_CAP,
  };
}

/** Rotation (rad) from model x/y to the scene, from the cell farthest from the first one. `positions` is x,y,z per cell in the scene. */
export function modelTheta(cells, positions) {
  const n = cells.length;
  let theta = 0;
  if (n > 1) {
    const a = cells[0];
    let far = 0, best = 0;
    for (let i = 1; i < n; i++) {
      const d = (cells[i].x - a.x) ** 2 + (cells[i].y - a.y) ** 2;
      if (d > best) { best = d; far = i; }
    }
    if (best > 0) {
      const dm = Math.atan2(cells[far].y - a.y, cells[far].x - a.x);
      const dw = Math.atan2(positions[far * 3 + 2] - positions[2], positions[far * 3] - positions[0]);
      theta = dw - dm;
    }
  }
  return theta;
}

/**
 * Column-major 4x4 matrices (16 floats per cell) that turn a unit box into each cell's block.
 * `positions` is x,y,z per cell in the scene (x east, y up, z north); the azimuth is modelTheta.
 * Sizes come from the cell extents.
 */
export function blockMatrices(cells, positions) {
  const n = cells.length;
  const theta = modelTheta(cells, positions);
  const cs = Math.cos(theta), sn = Math.sin(theta);
  const out = new Float32Array(n * 16);
  cells.forEach((c, i) => {
    const sx = Math.abs(c.x1 - c.x0), sy = Math.abs(c.y1 - c.y0), sh = Math.abs(c.bottom - c.top);
    const o = i * 16;
    out[o] = cs * sx; out[o + 2] = sn * sx; // model x axis in the scene
    out[o + 5] = sh; // vertical
    out[o + 8] = -sn * sy; out[o + 10] = cs * sy; // model y axis in the scene
    out[o + 12] = positions[i * 3]; out[o + 13] = positions[i * 3 + 1]; out[o + 14] = positions[i * 3 + 2];
    out[o + 15] = 1;
  });
  return out;
}

/**
 * Flat-scene positions (x east, y up, z north) of a selection's valid cells: y = -depth, or, for a
 * model with topography (cells carry `surface`), surface - depth - lowest surface so the hill stands
 * above the flat reference plane.
 */
export function flatPositions(sel) {
  const p = new Float32Array(sel.cells.length * 3);
  let base = Infinity;
  sel.cells.forEach((c) => { if (typeof c.surface === 'number' && c.surface < base) base = c.surface; });
  sel.cells.forEach((c, i) => {
    p[i * 3] = sel.east[i];
    p[i * 3 + 1] = (typeof c.surface === 'number' ? c.surface - base : 0) - c.depth;
    p[i * 3 + 2] = sel.north[i];
  });
  return p;
}
